import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";

const addDaysIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function CompleteTaskDialog({ task, contact, onClose, onDone }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [nextDate, setNextDate] = useState(addDaysIso(90));
  const [noNext, setNoNext] = useState(false);
  const [noNote, setNoNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (task) {
      setNextDate(addDaysIso(contact?.followup_days || 90));
      setNoNext(false); setNoNote("");
    }
  }, [task, contact]);

  if (!task) return null;

  const today = new Date().toLocaleDateString(lang === "pl" ? "pl-PL" : "en-US");

  const submit = async () => {
    setSaving(true);
    try {
      const payload = noNext
        ? { no_next_contact: true, no_next_note: noNote }
        : { next_followup_date: nextDate };
      await api.post(`/ops/tasks/${task.task_id}/complete`, payload);
      toast.success(t("ops.complete.saved"));
      onClose?.();
      onDone?.();
    } catch { toast.error(t("ops.form.fail")); }
    finally { setSaving(false); }
  };

  const cdName = contact ? `${contact.first_name} ${contact.last_name}`.trim() : "";

  return (
    <Dialog open={!!task} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="task-complete-dialog"
        className="max-w-lg bg-stone-950 border-stone-800">
        <DialogHeader>
          <DialogTitle className="serif text-2xl text-stone-100">
            {t("ops.complete.title")}
          </DialogTitle>
          {contact && (
            <DialogDescription className="text-stone-300 text-sm">
              {t("ops.complete.contact_line").replace("{name}", cdName)}
              <br />
              {t("ops.complete.last_set").replace("{date}", today)}
            </DialogDescription>
          )}
        </DialogHeader>

        {contact && !noNext && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="label-cap">{t("ops.complete.next_q")}</label>
              <input
                data-testid="complete-next-date"
                type="date" value={nextDate}
                onChange={(e) => setNextDate(e.target.value)}
                className="script-input w-full"
              />
            </div>
            <button
              onClick={() => setNoNext(true)}
              data-testid="complete-no-next"
              className="ghost-btn text-xs"
            >
              {t("ops.complete.no_next")}
            </button>
          </div>
        )}

        {contact && noNext && (
          <div className="mt-3 space-y-2">
            <label className="label-cap">{t("ops.complete.no_next_q")}</label>
            <textarea
              data-testid="complete-no-note"
              value={noNote}
              onChange={(e) => setNoNote(e.target.value)}
              rows={3}
              className="script-input w-full resize-none"
            />
            <button
              onClick={() => setNoNext(false)}
              className="ghost-btn text-xs"
            >
              ← {t("ops.complete.next_q")}
            </button>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-4">
          <button onClick={onClose} className="ghost-btn">{t("action.cancel")}</button>
          <button
            onClick={submit}
            data-testid="complete-submit"
            disabled={saving}
            className="pill-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {t("ops.complete.save")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
